// Authentication utility functions (shared x20 pattern).

/**
 * Decode a JWT without verifying its signature (for client-side expiry checks
 * only — the backend always verifies the signature).
 */
const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
};

/** Extract user details from the stored ID token. */
export const getUserInfoFromToken = () => {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const decoded = decodeJWT(token);
    if (!decoded) return null;
    return {
      userId: decoded.sub,
      email: decoded.email,
      emailVerified: decoded.email_verified,
      username: decoded["cognito:username"] || decoded.username,
      groups: decoded["cognito:groups"] || [],
      tokenExpiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
    };
  } catch (error) {
    console.error("Error extracting user info from token:", error);
    return null;
  }
};

/** Check if a JWT is expired (5-minute clock-skew buffer). */
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) return true;
    const expirationTime = decoded.exp * 1000;
    const buffer = 5 * 60 * 1000; // 5 minutes
    return Date.now() >= expirationTime - buffer;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
};

/** Close any open modals/overlays (used on logout). */
const closeAllModals = () => {
  const event = new CustomEvent("forceCloseAllModals", { detail: { reason: "logout" } });
  window.dispatchEvent(event);
  const modals = document.querySelectorAll(
    '[class*="modal"], [class*="Modal"], [class*="dialog"], [class*="Dialog"], [role="dialog"]',
  );
  modals.forEach((modal) => {
    if (modal.style) modal.style.display = "none";
  });
};

export const clearAuthState = () => {
  closeAllModals();
  try {
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("cognitoUser");
    localStorage.removeItem("lastActivityTime");
    Object.keys(localStorage).forEach((key) => {
      if (key.includes("cognito") || key.includes("amplify") || key.includes("aws")) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    /* localStorage unavailable (sandboxed context) */
  }
};

/** True only when a valid (non-expired) token is present. */
export const isAuthenticated = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return false;
  if (isTokenExpired(token)) {
    clearAuthState();
    return false;
  }
  return true;
};

/** Return the token only if valid; otherwise clear state and return null. */
export const getAuthToken = () => {
  const token = localStorage.getItem("jwtToken");
  if (!token) return null;
  if (isTokenExpired(token)) {
    clearAuthState();
    return null;
  }
  return token;
};

export const setAuthToken = (token) => {
  localStorage.setItem("jwtToken", token);
  localStorage.setItem("lastActivityTime", Date.now().toString());
};
